import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { Stack } from '@hub/stack'
import { Box } from '@hub/box'
import { Button } from '@hub/button'
import { Text } from '@hub/text'
import { Checkbox } from '@hub/checkbox'
import { Grid, GridItem } from '@hub/grid'
import { find } from 'lodash'
import { NextRouter, useRouter } from 'next/router'
import {
  Query,
  StoreListProps,
  StoreDetails,
} from '@scentregroup/shared/types/browse-stores'
import { CategoryList } from './category-list'
import { categoryList, subCategoryList, filterStores } from './helper'
import { isEntryDefinedAndNotNull } from '@scentregroup/shared/lib'
import { getAbsolutePathFromRouter } from '@scentregroup/shared/helpers/url-helpers'
import { ViewControl, ViewType } from '../view-control'
import { ActionSheetContainer, StoreDirectoryGrid } from '../stores'
import {
  FilterFormData,
  getFormdataFromQuery,
  RetailCategory,
} from '../stores/types'

function updateUrlSearchString(router: NextRouter, query: Query): void {
  const {
    category: _category,
    subCategory: _subcategory,
    categories: _categories,
    giftCardAccepted: _giftCardAccepted,
    searchTerm: _searchTerm,
    ...otherQueryParams
  } = router.query

  router.push(
    {
      pathname: router.pathname,
      query: {
        ...otherQueryParams,
        ...(query.giftCardAccepted
          ? { giftCardAccepted: query.giftCardAccepted }
          : {}),
        ...(query.searchTerm ? { searchTerm: query.searchTerm } : {}),
        ...(query.category
          ? {
              categories: [query.category.slug, query.subCategory?.slug].filter(
                el => Boolean(el)
              ) as string[],
            }
          : {}),
      },
    },
    undefined,
    {
      shallow: true,
    }
  )
}

const updateStores = (
  router: NextRouter,
  setStoreList: (stores: StoreDetails[]) => void,
  stores: StoreDetails[],
  newQuery: Query
): void => {
  updateUrlSearchString(router, newQuery)
  setStoreList(filterStores(stores, newQuery))
}

const handleOnChange = (
  router: NextRouter,
  setStoreList: (stores: StoreDetails[]) => void,
  retailCategories: RetailCategory[],
  stores: StoreDetails[],
  query: Query,
  queryType: string,
  value: string | boolean
): void => {
  const { category } = query
  if (queryType === 'category' && typeof value === 'string') {
    const cat = find(retailCategories, { title: value as string })
    const categories = [cat?.slug ?? '']
    router.push(
      getAbsolutePathFromRouter(
        router,
        { categories, page: undefined },
        { categories }
      )
    )
  } else if (queryType === 'subCategory' && typeof value === 'string') {
    const subCat = find(retailCategories, { title: value as string })
    const categories = [category?.slug || '', subCat?.slug ?? '']
    router.push(
      getAbsolutePathFromRouter(
        router,
        { categories, page: undefined },
        { categories }
      )
    )
  } else {
    updateStores(router, setStoreList, stores, {
      ...query,
      [queryType]: value,
    })
  }
}

export const StoreList: React.FC<React.PropsWithChildren<StoreListProps>> = ({
  stores,
  retailCategories,
  centre,
  query,
}) => {
  const router = useRouter()

  const formData = getFormdataFromQuery(router.query)
  const viewMode = formData.viewMode
  const sortBy = formData.sortBy

  const categories = useMemo(
    () => categoryList(retailCategories),
    [retailCategories]
  )

  const [storesView, setStoresView] = useState<ViewType>(
    query.viewMode === 'grid' || query.viewMode === 'list'
      ? query.viewMode
      : 'auto'
  )
  const [subcategories, setSubcategories] = useState<RetailCategory[]>([])

  const { category, subCategory, giftCardAccepted, searchTerm } = query

  const [giftCardAcceptedValue, setGiftCardAcceptedValue] = useState<boolean>(
    giftCardAccepted ?? false
  )

  const [storeList, setStoreList] = useState<StoreDetails[]>(stores)

  useEffect(() => {
    setStoreList(filterStores(stores, { giftCardAccepted, searchTerm, sortBy }))
  }, [stores, giftCardAccepted, searchTerm, sortBy])

  const handleClearAll = useCallback((): void => {
    updateStores(router, setStoreList, stores, {})
    setGiftCardAcceptedValue(false)
  }, [router, stores])

  const handleFormData = useCallback(
    (formData: FilterFormData): void => {
      const params = Object.entries(formData).map<[string, string | undefined]>(
        ([key, value]) => {
          if (key === 'sortBy' && value === 'asc') {
            return [key, undefined]
          }

          if (key === 'viewMode' && value === 'auto') {
            return [key, undefined]
          }

          if (key === 'viewMode' && value === 'grid') {
            setStoresView('grid')
          } else if (key === 'viewMode' && value === 'list') {
            setStoresView('list')
          }

          return [key, value]
        }
      )

      const overrideParams = {
        ...Object.fromEntries(
          params.filter(([_, value]) => value === undefined)
        ),
      }

      const extraParams = Object.fromEntries(
        params.filter(isEntryDefinedAndNotNull)
      )

      router.push(
        getAbsolutePathFromRouter(router, overrideParams, extraParams)
      )
    },
    [router]
  )

  useEffect(() => {
    if (category) {
      setSubcategories(subCategoryList(retailCategories, category.title))
    } else {
      setSubcategories([])
    }
  }, [category, retailCategories])

  const e = encodeURIComponent

  const transformedData = storeList.map(
    ({ id, spaceId, title, slug, categories, logoUrl }) => {
      return {
        _type: 'STORE' as const,
        id,
        spaceId,
        title,
        slug,
        retailCategories: categories ? categories.map(cat => cat.title) : [],
        storefrontUrl: `/${e(centre.slug)}/store/${e(id)}/${e(slug)}`,
        imageUrl: logoUrl ?? '',
        centreSlug: centre.slug,
      }
    }
  )

  return (
    <Stack>
      <Text
        sx={{
          fontSize: 'font-2xl',
          fontWeight: 'bold',
          marginBottom: 'spacing-lg',
        }}
      >
        Browse stores
      </Text>
      <Stack sx={{ marginBottom: 'spacing-lg' }}>
        <Grid
          sx={{
            gridGap: ['spacing-md'],
          }}
          templateColumns={[
            'repeat(1, 1fr)',
            'repeat(1, 1fr)',
            'repeat(3, 1fr)',
          ]}
        >
          <GridItem>
            {categories.length > 0 && (
              <CategoryList
                categories={categories}
                value={category?.title ?? ''}
                placeholder="Choose category"
                onChange={(value: string) =>
                  handleOnChange(
                    router,
                    setStoreList,
                    retailCategories,
                    stores,
                    query,
                    'category',
                    value
                  )
                }
              />
            )}
          </GridItem>
          <GridItem>
            {category && subcategories.length > 0 && (
              <CategoryList
                categories={subcategories}
                value={subCategory?.title ?? ''}
                placeholder="Choose subcategory"
                onChange={(value: string) =>
                  handleOnChange(
                    router,
                    setStoreList,
                    retailCategories,
                    stores,
                    query,
                    'subCategory',
                    value
                  )
                }
              />
            )}
          </GridItem>
        </Grid>
      </Stack>
      <Stack
        direction={['column', null, 'row']}
        shouldWrapChildren={false}
        sx={{
          alignItems: 'baseline',
          marginBottom: 'spacing-lg',
        }}
      >
        <Box sx={{ flexGrow: 1 }}>
          <Checkbox
            key="GiftCards"
            isChecked={giftCardAcceptedValue}
            sx={{ marginRight: 'spacing-md' }}
            onChange={() => {
              setGiftCardAcceptedValue(!giftCardAcceptedValue)
              handleOnChange(
                router,
                setStoreList,
                retailCategories,
                stores,
                query,
                'giftCardAccepted',
                !giftCardAcceptedValue
              )
            }}
          >
            Gift cards
          </Checkbox>
          <Button
            sx={{
              textDecoration: 'underline',
              fontWeight: 'normal',
              padding: 'spacing-none',
              height: 'spacing-md',
              textTransform: 'none',
              color: 'licorice',
            }}
            variant="link"
            onClick={handleClearAll}
          >
            Clear all
          </Button>
        </Box>
        <Stack
          direction={['column', null, 'row']}
          shouldWrapChildren={false}
          sx={{
            rowGap: 'spacing-md',
            width: ['100%', null, '400px'],
            paddingTop: ['spacing-lg', null, 'spacing-0'],
            paddingBottom: ['spacing-md', null, 'spacing-0'],
            flexGrow: 0,
          }}
        >
          <ActionSheetContainer data={formData} onChange={handleFormData} />
          <ViewControl
            value={viewMode}
            onChange={viewMode => handleFormData({ ...formData, viewMode })}
            sx={{
              flexGrow: 0,
              display: ['none', null, 'flex'],
              marginLeft: 'spacing-md !important',
            }}
          />
        </Stack>
      </Stack>
      {transformedData.length > 0 ? (
        <StoreDirectoryGrid items={transformedData} view={storesView} />
      ) : (
        <Box
          sx={{
            marginTop: 'spacing-md',
            textAlign: 'center',
          }}
        >
          <Text
            sx={{
              fontSize: 'font-2xl',
              fontWeight: 'bold',
              marginBottom: 'spacing-sm',
            }}
          >
            No matching stores
          </Text>
          <Text>Try changing categories or removing filters</Text>
        </Box>
      )}
    </Stack>
  )
}
